import React from 'react'
import {
  Box,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import ViewListIcon from '@mui/icons-material/ViewList'
import ViewModuleIcon from '@mui/icons-material/ViewModule'
import SortIcon from '@mui/icons-material/Sort'
import ClearIcon from '@mui/icons-material/Clear'

export type ViewMode = 'grid' | 'list'
export type SortOption = 'createdAt,desc' | 'createdAt,asc' | 'title,asc' | 'title,desc' | 'bytes,desc' | 'bytes,asc'

type Props = {
  searchQuery: string
  onSearchChange: (query: string) => void
  viewMode: ViewMode
  onViewModeChange: (mode: ViewMode) => void
  sortOption: SortOption
  onSortChange: (sort: SortOption) => void
  totalFiles: number
}

export default function Toolbar({
  searchQuery,
  onSearchChange,
  viewMode,
  onViewModeChange,
  sortOption,
  onSortChange,
  totalFiles
}: Props) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        py: 1.5,
        px: 2,
        bgcolor: '#fff',
        borderRadius: 2,
        border: '1px solid #e0e0e0',
        flexWrap: 'wrap'
      }}
    >
      {/* Campo de busca */}
      <TextField
        placeholder="Buscar arquivos..."
        value={searchQuery}
        onChange={e => onSearchChange(e.target.value)}
        size="small"
        sx={{
          flex: 1,
          minWidth: 200,
          maxWidth: 400,
          '& .MuiOutlinedInput-root': {
            borderRadius: 3,
            bgcolor: '#f5f5f5',
            '&:hover': { bgcolor: '#eeeeee' },
            '&.Mui-focused': { bgcolor: '#fff' }
          }
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: 'action.active' }} />
            </InputAdornment>
          ),
          endAdornment: searchQuery ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => onSearchChange('')}>
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null
        }}
      />

      {/* Contador de arquivos */}
      <Typography variant="body2" sx={{ color: '#666', whiteSpace: 'nowrap' }}>
        {totalFiles} {totalFiles === 1 ? 'arquivo' : 'arquivos'}
      </Typography>

      <Box sx={{ flex: 1 }} />

      {/* Ordenação */}
      <FormControl size="small" sx={{ minWidth: 180 }}>
        <Select
          value={sortOption}
          onChange={e => onSortChange(e.target.value as SortOption)}
          displayEmpty
          startAdornment={<SortIcon sx={{ mr: 1, color: 'action.active' }} />}
          sx={{
            borderRadius: 2,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' }
          }}
        >
          <MenuItem value="createdAt,desc">Mais recentes</MenuItem>
          <MenuItem value="createdAt,asc">Mais antigos</MenuItem>
          <MenuItem value="title,asc">Nome A-Z</MenuItem>
          <MenuItem value="title,desc">Nome Z-A</MenuItem>
          <MenuItem value="bytes,desc">Maior tamanho</MenuItem>
          <MenuItem value="bytes,asc">Menor tamanho</MenuItem>
        </Select>
      </FormControl>

      {/* Toggle de visualização */}
      <ToggleButtonGroup
        value={viewMode}
        exclusive
        onChange={(_, mode) => mode && onViewModeChange(mode)}
        size="small"
        sx={{
          '& .MuiToggleButton-root': {
            border: '1px solid #e0e0e0',
            '&.Mui-selected': {
              bgcolor: '#e3f2fd',
              color: '#1976d2'
            }
          }
        }}
      >
        <ToggleButton value="list">
          <Tooltip title="Visualizar em lista">
            <ViewListIcon />
          </Tooltip>
        </ToggleButton>
        <ToggleButton value="grid">
          <Tooltip title="Visualizar em grade">
            <ViewModuleIcon />
          </Tooltip>
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>
  )
}
